/**
 * MovementsHeatmap — GitHub-contributions-style 1-year activity grid.
 *
 * iter-188 redesign — Compared with the previous version this:
 *   • adds month labels (Ene, Feb, …) above the grid so users can
 *     locate quickly without counting columns,
 *   • adds day-of-week tickers (L M X J V) on the left edge,
 *   • makes each cell CLICKABLE → navigates to /history?date=YYYY-MM-DD
 *     so the user can drill into the actual movements,
 *   • marks "today" with a subtle ring so the user has an anchor,
 *   • adds top-line stats (total movements, total income, expense),
 *   • localizes copy in EN/ES/PT (previously only EN/ES),
 *   • surfaces an empty state when the user has zero activity in the
 *     window (the old version showed an all-pale grid with no hint).
 *
 * Endpoint: GET /api/calendar/heatmap?days=365&mode=…
 *
 * Design notes:
 *   • Columns = aligned weeks (~53 in a year); rows = day of week.
 *   • Empty cells (no movements) get a neutral light-slate background
 *     so the overall grid shape is visible even on quiet weeks.
 *   • Tooltips use native `title` for zero-JS dependency.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Activity, ArrowDown, ArrowUp, TrendingUp } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, formatMoney } from "../lib/api";
import { useProfile } from "../hooks/queries";

const COOL_RAMP = ["#F1F5F9", "#BBF7D0", "#86EFAC", "#4ADE80", "#16A34A"];
const IN_RAMP   = ["#F1F5F9", "#BAE6FD", "#7DD3FC", "#38BDF8", "#0284C7"];
const OUT_RAMP  = ["#F1F5F9", "#FECACA", "#FCA5A5", "#F87171", "#DC2626"];

const MODES = [
  {
    key: "movements",
    label: { es: "Todo", en: "All", pt: "Tudo" },
    ramp: COOL_RAMP,
    Icon: Activity,
  },
  {
    key: "cash_in",
    label: { es: "Ingresos", en: "Income", pt: "Receitas" },
    ramp: IN_RAMP,
    Icon: ArrowDown,
  },
  {
    key: "cash_out",
    label: { es: "Gastos", en: "Outflow", pt: "Despesas" },
    ramp: OUT_RAMP,
    Icon: ArrowUp,
  },
];

const MONTHS_BY_LANG = {
  es: ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  pt: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
};

const DOW_BY_LANG = {
  es: ["D", "L", "M", "X", "J", "V", "S"],
  en: ["S", "M", "T", "W", "T", "F", "S"],
  pt: ["D", "S", "T", "Q", "Q", "S", "S"],
};

function bucketColor(count, max, ramp) {
  if (!count) return ramp[0];
  if (max <= 1) return ramp[2];
  const ratio = count / max;
  if (ratio < 0.25) return ramp[1];
  if (ratio < 0.5)  return ramp[2];
  if (ratio < 0.75) return ramp[3];
  return ramp[4];
}

function formatLocalDate(iso, lang) {
  const d = new Date(`${iso}T12:00:00Z`);
  const locale = lang === "en" ? "en-US" : lang === "pt" ? "pt-BR" : "es-ES";
  return d.toLocaleDateString(locale, {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

export function MovementsHeatmap({ lang }) {
  const navigate = useNavigate();
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const langKey = lang === "en" || lang === "pt" ? lang : "es";
  const [mode, setMode] = useState("movements");
  const modeDef = MODES.find((m) => m.key === mode) || MODES[0];

  const { data, isLoading } = useQuery({
    queryKey: ["heatmap", mode],
    queryFn: async () => (await api.get(`/calendar/heatmap`, { params: { days: 365, mode } })).data,
    staleTime: 60_000,
  });

  const todayIso = new Date().toISOString().slice(0, 10);

  // Build a 53-week × 7-day grid keyed by ISO day. Empty days remain in
  // the grid (we want the visual shape) but with count=0. We also
  // capture which column each new month begins on, so we can render
  // month labels on top of the grid aligned to those columns.
  const { weeks, monthLabels } = useMemo(() => {
    if (!data) return { weeks: null, monthLabels: [] };
    const byDate = new Map((data.days || []).map((d) => [d.date, d]));
    const end = new Date(`${data.to_date}T12:00:00Z`);
    const start = new Date(`${data.from_date}T12:00:00Z`);
    start.setUTCDate(start.getUTCDate() - start.getUTCDay());

    const grid = [];
    const monthCols = [];
    let cursor = new Date(start);
    let lastMonth = -1;
    let safety = 60;
    let colIdx = 0;
    while (cursor <= end && safety > 0) {
      const week = [];
      let weekMonth = null;
      for (let dow = 0; dow < 7; dow++) {
        const inWindow = cursor >= new Date(`${data.from_date}T00:00:00Z`) && cursor <= end;
        const iso = cursor.toISOString().slice(0, 10);
        const cell = byDate.get(iso);
        const monthOfDay = cursor.getUTCMonth();
        if (inWindow && weekMonth === null) {
          weekMonth = monthOfDay;
        }
        week.push({ iso, inWindow, monthOfDay, ...(cell || { count: 0, total_in: 0, total_out: 0 }) });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      grid.push(week);
      if (weekMonth !== null && weekMonth !== lastMonth) {
        monthCols.push({ col: colIdx, month: weekMonth });
        lastMonth = weekMonth;
      }
      colIdx += 1;
      safety -= 1;
    }
    return { weeks: grid, monthLabels: monthCols };
  }, [data]);

  // Aggregated summary across the whole window — gives the user one
  // glance number per metric instead of forcing them to scan the grid.
  const totals = useMemo(() => {
    if (!data?.days) return null;
    return data.days.reduce(
      (acc, d) => {
        acc.count += d.count || 0;
        acc.cash_in += d.total_in || 0;
        acc.cash_out += d.total_out || 0;
        return acc;
      },
      { count: 0, cash_in: 0, cash_out: 0 },
    );
  }, [data]);

  const onCellClick = (cell) => {
    if (!cell.inWindow || cell.count === 0) return;
    navigate(`/history?date=${cell.iso}`);
  };

  return (
    <Card className="p-5 rounded-2xl border-slate-200" data-testid="movements-heatmap">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <modeDef.Icon className="h-4 w-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-800">
              {{ es: "Actividad anual", en: "Yearly activity", pt: "Atividade anual" }[langKey]}
            </h2>
          </div>
          <p className="text-[11px] text-slate-500 max-w-md">
            {{
              es: "Una celda por día. Tocá un día para ver sus movimientos. La intensidad se mide relativa a tu día más activo.",
              en: "One cell per day. Tap a day to see its movements. Intensity is relative to your busiest day.",
              pt: "Uma célula por dia. Toque um dia para ver seus movimentos. A intensidade é relativa ao seu dia mais ativo.",
            }[langKey]}
          </p>
        </div>
        <div className="flex gap-1.5" data-testid="heatmap-mode-toggle">
          {MODES.map((m) => (
            <Button
              key={m.key}
              size="sm"
              variant={m.key === mode ? "default" : "outline"}
              onClick={() => setMode(m.key)}
              data-testid={`heatmap-mode-${m.key}`}
              className="h-7 text-[11px]"
            >
              <m.Icon className="h-3 w-3 mr-1" />
              {m.label[langKey]}
            </Button>
          ))}
        </div>
      </div>

      {/* Top-line summary stats */}
      {totals && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="heatmap-summary">
          <div className="p-2.5 rounded-lg bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {{ es: "Movimientos", en: "Movements", pt: "Movimentações" }[langKey]}
            </div>
            <div className="text-lg font-bold tabular-nums text-slate-800">{totals.count}</div>
          </div>
          <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
              {{ es: "Ingresos", en: "Income", pt: "Receitas" }[langKey]}
            </div>
            <div className="text-lg font-bold tabular-nums text-emerald-700">
              {formatMoney(totals.cash_in, currency)}
            </div>
          </div>
          <div className="p-2.5 rounded-lg bg-red-50 border border-red-200">
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold">
              {{ es: "Gastos", en: "Outflow", pt: "Despesas" }[langKey]}
            </div>
            <div className="text-lg font-bold tabular-nums text-red-700">
              {formatMoney(totals.cash_out, currency)}
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-6 text-xs text-slate-400">…</div>
      ) : !weeks ? null : totals && totals.count === 0 ? (
        <div
          className="text-center py-10 px-4 rounded-xl border border-dashed border-slate-200"
          data-testid="heatmap-empty"
        >
          <TrendingUp className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-600">
            {{ es: "Sin actividad todavía", en: "No activity yet", pt: "Sem atividade ainda" }[langKey]}
          </p>
          <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
            {{
              es: "Cuando registres movimientos durante el año, las celdas comenzarán a iluminarse aquí.",
              en: "Once you record movements through the year, cells will start to light up here.",
              pt: "Quando você registrar movimentos durante o ano, as células começarão a se iluminar aqui.",
            }[langKey]}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto" data-testid="heatmap-grid-scroll">
            {/* Wrapper with day-of-week column + scrollable grid+months */}
            <div className="inline-flex gap-2" style={{ minWidth: "max-content" }}>
              {/* Day-of-week labels (left). Showing every other row keeps
                  the column compact while remaining readable. */}
              <div className="flex flex-col gap-[3px] pt-5 text-[9px] text-slate-400 font-medium leading-3">
                {DOW_BY_LANG[langKey].map((d, i) => (
                  <span key={i} className={`h-3 ${i % 2 === 0 ? "opacity-100" : "opacity-0"}`}>{d}</span>
                ))}
              </div>
              <div className="flex flex-col">
                {/* Month labels row */}
                <div className="relative h-4 mb-1" data-testid="heatmap-months">
                  {monthLabels.map(({ col, month }, i) => (
                    <span
                      key={i}
                      className="absolute text-[10px] text-slate-500 font-medium"
                      style={{ left: `${col * 15}px` }}  // 12px cell + 3px gap
                    >
                      {MONTHS_BY_LANG[langKey][month]}
                    </span>
                  ))}
                </div>
                {/* Cells grid */}
                <div className="inline-flex gap-[3px]">
                  {weeks.map((week, wi) => (
                    <div key={wi} className="flex flex-col gap-[3px]">
                      {week.map((cell, di) => {
                        const isToday = cell.iso === todayIso;
                        const isClickable = cell.inWindow && cell.count > 0;
                        const bg = cell.inWindow ? bucketColor(cell.count, data.max_count, modeDef.ramp) : "transparent";
                        return (
                          <button /* design-system: ALLOW legacy-todo */
                            key={`${wi}-${di}`}
                            type="button"
                            data-testid={isClickable ? `heatmap-cell-${cell.iso}` : undefined}
                            onClick={() => onCellClick(cell)}
                            disabled={!isClickable}
                            className={`h-3 w-3 rounded-sm transition-transform ${
                              isClickable ? "cursor-pointer hover:scale-125" : "cursor-default"
                            } ${isToday ? "ring-1 ring-slate-700" : ""}`}
                            style={{ backgroundColor: bg }}
                            title={
                              cell.inWindow
                                ? `${formatLocalDate(cell.iso, langKey)} · ${cell.count} ${
                                    { es: "movimientos", en: "movements", pt: "movimentações" }[langKey]
                                  }`
                                : ""
                            }
                            aria-label={cell.inWindow ? `${cell.iso}: ${cell.count}` : undefined}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 text-[10px] text-slate-500">
            <Badge variant="outline" data-testid="heatmap-total">
              {{ es: "Pico:", en: "Peak:", pt: "Pico:" }[langKey]} {data.max_count}/
              {{ es: "día", en: "day", pt: "dia" }[langKey]}
            </Badge>
            <div className="flex items-center gap-1">
              <span>{{ es: "Menos", en: "Less", pt: "Menos" }[langKey]}</span>
              {modeDef.ramp.map((c, i) => (
                <span key={i} className="h-3 w-3 rounded-sm" style={{ backgroundColor: c }} />
              ))}
              <span>{{ es: "Más", en: "More", pt: "Mais" }[langKey]}</span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
